import { Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { useForceErpSync } from "../useCases/useForceErpSync";
import { useAuth } from "@belray-ventec/platform-session";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/ui/chakra/dialog";
import { toast } from "@/ui/chakra/toaster";

interface ConfirmDialogSyncAllStockProps {
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: "En cola, esperando al servidor de sincronización...",
  RUNNING: "Consultando Totvs y actualizando los productos...",
};

const ConfirmDialogSyncAllStock: React.FC<ConfirmDialogSyncAllStockProps> = ({
  isOpen,
  onClose,
}) => {
  const { session } = useAuth();

  const { start, isPending, status } = useForceErpSync({
    onDone: (job) => {
      toast({
        title: "Stock sincronizado",
        description: `Se consultó Totvs y se actualizó el stock de ${job.syncedCount ?? 0} producto(s) de tu zona.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onClose();
    },
    onError: (message) => {
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 8000,
        isClosable: true,
      });
    },
  });

  const handleConfirm = () => {
    if (!session?.zone) return;

    start(session.zone, {
      onError: () => {
        toast({
          title: "Error",
          description:
            "No se pudo solicitar la sincronización. Intente nuevamente.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const handleClose = () => {
    if (isPending) {
      toast({
        title: "Sincronización en curso",
        description:
          "Seguirá ejecutándose en segundo plano. Actualiza la página en unos minutos para ver el stock al día.",
        status: "info",
        duration: 6000,
        isClosable: true,
      });
    }
    onClose();
  };

  return (
    <DialogRoot isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <DialogContent>
        <DialogHeader pb={2}>
          <DialogTitle>Sincronizar stock con Totvs</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />

        <DialogBody pt={2} pb={6}>
          <Text color="fg.muted">
            Se consultará el stock actual en{" "}
            <Text as="span" fontWeight="bold">
              Totvs
            </Text>{" "}
            para las bodegas de tu zona y se actualizará el stock del sistema
            con lo que responda el ERP.
          </Text>

          {isPending && (
            <Flex
              align="center"
              gap={3}
              mt={5}
              bg="bg.subtle"
              p={4}
              borderRadius="lg"
            >
              <Spinner size="sm" color="brand.solid" />
              <Text color="fg" fontSize="sm">
                {STATUS_LABELS[status ?? "PENDING"] ?? STATUS_LABELS.PENDING}
              </Text>
            </Flex>
          )}

          {isPending && (
            <Text color="fg.muted" fontSize="xs" mt={3}>
              Puede tardar un par de minutos. Puedes cerrar esta ventana, la
              sincronización continúa.
            </Text>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {isPending ? "Cerrar" : "Cancelar"}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleConfirm}
            loading={isPending}
            loadingText="Sincronizando..."
          >
            Sincronizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ConfirmDialogSyncAllStock;
